import { GetServerSideProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { Montserrat } from 'next/font/google';
import SEOHead from '../components/SEOHead';
import { useRouter } from 'next/router';
import { FiArrowRight } from 'react-icons/fi';
import { RiArrowGoBackLine, RiRocket2Line, RiShieldCheckLine, RiVipCrown2Line } from 'react-icons/ri';
import { useTheme } from '../lib/ThemeContext';
import FeaturedProductSpotlight, { FeaturedProduct } from '../components/home/FeaturedProductSpotlight';
import { getShopCatalogData } from '../lib/commerce/catalog';

const montserrat = Montserrat({
  subsets: ['latin'],
  weight: ['400', '600', '700', '800'],
});

type Category = {
  id: string;
  name: string;
  nameLt?: string;
  slug: string;
  description?: string;
  descriptionLt?: string;
  imageUrl?: string;
  productCount?: number;
  filterBy?: 'category' | 'parent';
};

type HomeProps = {
  categories: Category[];
  featuredProduct: FeaturedProduct | null;
  isMobileSSR?: boolean;
};

const BANNERS = [
  '/GadgetFever/banners (1).webp',
  '/GadgetFever/banners (2).webp',
  '/GadgetFever/banners (3).webp',
  '/GadgetFever/banners (4).webp',
];

const FALLBACK_CATEGORIES: Category[] = [
  { id: '1', name: 'Smartphones', slug: 'smartphones', description: 'Latest flagship and budget phones', imageUrl: '/GadgetFever/banners (1).webp', filterBy: 'category' },
  { id: '2', name: 'Laptops', slug: 'laptops', description: 'Powerful laptops for work and gaming', imageUrl: '/GadgetFever/banners (2).webp', filterBy: 'category' },
  { id: '3', name: 'Home Electronics', slug: 'home-electronics', description: 'Smart home devices and appliances', imageUrl: '/GadgetFever/banners (3).webp', filterBy: 'category' },
  { id: '4', name: 'Wearables', slug: 'wearables', description: 'Smartwatches, earbuds and fitness trackers', imageUrl: '/GadgetFever/banners (4).webp', filterBy: 'category' },
];

const toSlug = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getParentName = (categoryName: string): string => {
  const raw = String(categoryName || '').trim();
  if (!raw) return 'Other';
  if (raw.includes('>')) return raw.split('>')[0].trim();
  if (raw.includes('›')) return raw.split('›')[0].trim();
  return raw;
};

const CATEGORY_IMAGE_OVERRIDES: Record<string, string> = {
  'car-multimedia': '/GadgetFever/car-multimedia.webp',
  'car-multimedia-player': '/GadgetFever/car-multimedia.webp',
  'headphones': '/GadgetFever/headphones.webp',
  'earphones-headphones': '/GadgetFever/headphones.webp',
  'smart-watches': '/GadgetFever/smart-watches.webp',
  'speakers': '/GadgetFever/speakers.webp',
  'automobiles-motorcycles': '/GadgetFever/car-multimedia.webp',
  'phones-accessories': '/GadgetFever/headphones.webp',
  'consumer-electronics': '/GadgetFever/speakers.webp',
  wearables: '/GadgetFever/smart-watches.webp',
};

const pickCategoryImage = (slug: string, name: string, fallback?: string): string | undefined => {
  const direct = CATEGORY_IMAGE_OVERRIDES[slug];
  if (direct) return direct;
  const normalizedName = toSlug(name);
  if (normalizedName && CATEGORY_IMAGE_OVERRIDES[normalizedName]) {
    return CATEGORY_IMAGE_OVERRIDES[normalizedName];
  }
  return fallback;
};

const normalizeImageUrl = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        const first = parsed.find((item) => typeof item === 'string' && item.trim().length > 0);
        if (typeof first === 'string') return first.trim();
      }
    } catch {
      return undefined;
    }
  }

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
    return trimmed;
  }

  return undefined;
};

const TRUST_FEATURES = [
  { icon: RiRocket2Line, title: 'Fast Shipping', desc: 'Delivered to your door in 7-15 business days' },
  { icon: RiShieldCheckLine, title: 'Secure Payments', desc: 'SSL encrypted checkout via Stripe' },
  { icon: RiArrowGoBackLine, title: 'Easy Returns', desc: '30-day hassle-free return policy' },
  { icon: RiVipCrown2Line, title: 'Quality Guaranteed', desc: 'Curated products from verified suppliers' },
];

export default function Home({ categories, featuredProduct }: HomeProps) {
  const { theme } = useTheme();
  const router = useRouter();
  const isLt = router.asPath.startsWith('/lt');
  const displayCategories = categories.length > 0 ? categories : FALLBACK_CATEGORIES;
  const heroImage =
    theme === 'dark'
      ? '/GadgetFever/dark_mode_gadgetfever.webp'
      : '/GadgetFever/banners (1).webp';

  const productPageHref = (product: { id: string; slug?: string; title: string }): string => {
    if (product.slug) return `/product/${product.slug}`;
    const slug = product.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60);
    return `/product/${slug ? `${slug}-${product.id.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 8)}` : product.id}`;
  };

  return (
    <>
      <SEOHead
        title="GadgetFever - Electronics & Gadgets Store"
        description="Shop the latest smartphones, laptops, home electronics and wearables. Fast shipping across Europe. Best prices guaranteed."
        canonicalUrl="/"
        lang="en"
        ogImage="/GadgetFever/GadgetFever_webp_logo.webp"
      />

      <main>

        {/* Hero */}
        <section className="relative w-full overflow-hidden min-h-screen flex items-center">
          <Image
            src={heroImage}
            alt="GadgetFever - Electronics Store"
            fill
            priority
            className="object-cover"
            sizes="100vw"
          />
          <div
            className={`absolute inset-0 ${
              theme === 'dark'
                ? 'bg-gradient-to-r from-black/45 via-black/30 to-black/10'
                : 'bg-gradient-to-r from-white/22 via-white/12 to-transparent'
            }`}
          />
          <div className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-20 sm:pt-36 sm:pb-28 ${montserrat.className}`}>
            <div className={`max-w-2xl ${theme === 'light' ? 'bg-white/75 backdrop-blur-xl rounded-3xl p-8 sm:p-10' : ''}`}>
              <div className="flex items-center gap-3 mb-6">
                <Image
                  src="/GadgetFever/GadgetFever_webp_logo.webp"
                  alt="GadgetFever Logo"
                  width={72}
                  height={72}
                  className="rounded-xl"
                />
                <span className={`text-2xl font-extrabold tracking-tight ${theme === 'dark' ? 'text-white drop-shadow-lg' : 'text-gray-800'}`}>
                  GadgetFever
                </span>
              </div>
              <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-4 ${theme === 'dark' ? 'text-white drop-shadow-lg' : 'text-gray-800'}`}>
                The Latest Tech,{' '}
                <span className={theme === 'dark' ? 'text-white' : 'text-gray-800'}>Delivered Fast</span>
              </h1>
              <p className={`text-lg mb-8 leading-relaxed ${theme === 'dark' ? 'text-white drop-shadow-md' : 'text-gray-950'}`}>
                Shop smartphones, laptops, home electronics and wearables.
                Curated products, competitive prices, fast EU shipping.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  href={isLt ? '/lt/parduotuve' : '/shop'}
                  className="inline-flex items-center px-6 py-3 rounded-xl bg-black hover:bg-black/85 text-white font-semibold transition-colors shadow-lg shadow-black/25"
                >
                  Shop Now
                </Link>
                <Link
                  href={isLt ? '/lt/kontaktai' : '/contact'}
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-colors backdrop-blur-sm ${theme === 'dark' ? 'bg-white/20 hover:bg-white/30 text-white border border-white/40' : 'bg-black/20 hover:bg-black/30 text-gray-950 border border-gray-950'}`}
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="w-full bg-[#e8e8e8] dark:bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
            <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white mb-2">
              Shop by Category
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Find exactly what you are looking for
            </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {displayCategories.slice(0, 4).map((cat, idx) => (
                <Link
                  key={cat.id}
                  href={
                    isLt
                      ? `/lt/parduotuve?${cat.filterBy === 'parent' ? 'parent' : 'category'}=${encodeURIComponent(cat.slug)}`
                      : `/shop?${cat.filterBy === 'parent' ? 'parent' : 'category'}=${encodeURIComponent(cat.slug)}`
                  }
                  className="group relative overflow-hidden rounded-2xl bg-gray-100 dark:bg-[rgba(13,17,23,0.6)] border border-gray-200/60 dark:border-[rgba(48,54,61,0.5)] hover:shadow-xl hover:shadow-black/10 transition-all duration-300 hover:-translate-y-1 aspect-[4/3]"
                >
                  <Image
                    src={cat.imageUrl || BANNERS[idx % BANNERS.length]}
                    alt={cat.name}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-bold text-lg leading-tight">{cat.name}</h3>
                    {cat.description && (
                      <p className="text-gray-300 text-xs mt-1 line-clamp-2">{cat.description}</p>
                    )}
                    {cat.productCount != null && cat.productCount > 0 && (
                      <span className="inline-block mt-2 text-xs text-sand-400 font-medium">
                        {cat.productCount} products
                      </span>
                    )}
                  </div>
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="flex items-center gap-1 bg-primary text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                      Shop <FiArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                href={isLt ? '/lt/parduotuve' : '/shop'}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-gray-200 dark:border-[rgba(48,54,61,0.6)] text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-sm"
              >
                View All Products <FiArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {featuredProduct && (
          <FeaturedProductSpotlight
            product={featuredProduct}
            productHref={productPageHref(featuredProduct)}
          />
        )}

        {/* Banner strip */}
        <section className="w-full bg-[#e8e8e8] dark:bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-12">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {BANNERS.slice(1, 4).map((src, i) => (
                <div key={i} className="relative rounded-2xl overflow-hidden aspect-video bg-gray-100 dark:bg-gray-800">
                  <Image
                    src={src}
                    alt={`GadgetFever banner ${i + 2}`}
                    fill
                    className="object-cover"
                    sizes="(max-width: 640px) 100vw, 33vw"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust features */}
        <section className="bg-[#e8e8e8] dark:bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className={`h-px w-full opacity-80 ${theme === 'dark' ? 'bg-[#a29986]' : 'bg-[#8b6f47]'}`} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {TRUST_FEATURES.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex flex-col items-center text-center gap-3">
                  <div className="w-14 h-14 flex items-center justify-center">
                    <Icon className="w-8 h-8 text-primary dark:text-sand-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white text-sm">{title}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async (ctx) => {
  const ua = ctx.req.headers['user-agent'] || '';
  const isMobileSSR = /mobile|android|iphone|ipad/i.test(ua);

  let categories: Category[] = [];
  let featuredProduct: FeaturedProduct | null = null;

  try {
    const catalog = await getShopCatalogData();
    const catalogProducts = (catalog.products || []).filter((p) => p.active && p.stock > 0);

    const byParent = new Map<string, { name: string; count: number; imageUrl?: string }>();
    for (const row of catalogProducts) {
      const parentName = getParentName(row.parentName || row.categoryName || '');
      const parentSlug = toSlug(parentName);
      if (!parentSlug) continue;
      const current = byParent.get(parentSlug) ?? { name: parentName, count: 0, imageUrl: undefined };
      current.count += 1;
      const normalizedImage = normalizeImageUrl(row.imageUrl);
      if (!current.imageUrl && normalizedImage) {
        current.imageUrl = normalizedImage;
      }
      byParent.set(parentSlug, current);
    }

    categories = Array.from(byParent.entries())
      .map(([slug, meta]) => ({
        id: slug,
        name: meta.name,
        slug,
        description: 'Explore products in this category',
        imageUrl: pickCategoryImage(slug, meta.name, meta.imageUrl),
        productCount: meta.count,
        filterBy: 'parent' as const,
      }))
      .sort((a, b) => (b.productCount ?? 0) - (a.productCount ?? 0));

    if (catalogProducts.length > 0) {
      const randomRow = catalogProducts[Math.floor(Math.random() * catalogProducts.length)] as any;
      featuredProduct = {
        id: String(randomRow.id || ''),
        slug: String(randomRow.slug || ''),
        title: String(randomRow.title || 'Featured Product'),
        imageUrl: normalizeImageUrl(randomRow.imageUrl) || '/no-image.png',
        price: Number(randomRow.price ?? 0),
        categoryName: String(getParentName(randomRow.categoryName || '') || ''),
        discountPercent: randomRow.discountPercent ?? null,
      };
    }
  } catch {
    // no catalog categories — fallback shown
  }

  return { props: { categories, featuredProduct, isMobileSSR } };
};
